import { type AutomationTaskWithoutTriggers } from "../../../entities/Automation";
import {
  type CrossSellProductInfo,
  type Currency,
  type PricePaymentType,
  type ProductChargeTypeEnum,
  type ProductSubscriptionInterval,
  type ProductTypeEnum,
} from "../../../entities/Product";
import { type ClientArchiveReason } from "../../../entities/ProductClient";
import { type GenericBusinessEventDto } from "../../BusinessEventDto";
import { type GenericBusinessEvent } from "../BusinessEventEnvelope";

export enum ProductEventType {
  ProductPurchased = "fitness-app.product.product-purchased",
  ProductCreated = "fitness-app.product.product-created",
  ProductAccessRescheduled = "fitness-app.product.product-access-rescheduled",
  ProductSubscriptionRenewed = "fitness-app.product.product-subscription-renewed",
  ProductSubscriptionPlanUpdated = "fitness-app.product.product-subscription-plan-updated",
  ProductAccessProlonged = "fitness-app.product.product-access-prolonged",
  ProductAccessRenewed = "fitness-app.product.product-access-renewed",
  ProductSubscriptionEnd = "fitness-app.product.product-subscription-end",
  ProductAccessPeriodEnd = "fitness-app.product.product-access-period-end",
  ProductClientImported = "fitness-app.product.client-imported",
  ProductClientsImportFinished = "fitness-app.product.clients-import-finished",
  ProductSubscriptionCancelAtPeriodEnd = "fitness-app.product.product-subscription-cancel-at-period-end",
  ProductSubscriptionResumeAtPeriodEnd = "fitness-app.product.product-subscription-resume-at-period-end",
  ProductAccessPeriodEndingIn14Days = "fitness-app.product.product-access-period-ending-in-14-days",
  ProductAccessPeriodEndingIn7Days = "fitness-app.product.product-access-period-ending-in-7-days",
  ProductAccessPeriodEndingIn3Days = "fitness-app.product.product-access-period-ending-in-3-days",
  ProductAccessPeriodEndingIn1Day = "fitness-app.product.product-access-period-ending-in-1-day",
  ProductClientArchived = "fitness-app.product.product-client-archived",
  ProductClientEmailChanged = "fitness-app.product.product-client-email-changed",
  ProductSlugChanged = "fitness-app.product.product-slug-changed",
  ProductClientRemoved = "fitness-app.product.product-client-removed",
  ProductPaymentFailed = "fitness-app.product.product-payment-failed",
  ProductManualPaymentCreated = "fitness-app.product.product-manual-payment-created",
  ProductManualPaymentForNextCycleCreated = "fitness-app.product.product-manual-payment-for-next-cycle-created",
  ProductManualPaymentExpired = "fitness-app.product.product-manual-payment-expired",
  ProductInstallmentsPaidOff = "fitness-app.product.product-installments-paid-off",
}

export type ProductPurchasedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductPurchased,
  {
    productId: string;
    customerId: string;
    clientEmail: string;
    priceId: string | null;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    isFree: boolean;
    orderId: string;
    paymentType: PricePaymentType | null;
    discounted?: boolean;
    ownerId: string;
    price: null | { amount: number; currency: Currency };
    refCode?: string | null;
    productType: ProductTypeEnum;
    clientRefCode?: string | null;
    productSlug: string | null;
    sessionId: string | null;
    isCrossSell?: boolean;
    crossSell?: CrossSellProductInfo[];
    checkoutVariantId: string | null;
  }
>;

export type ProductSubscriptionRenewedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductSubscriptionRenewed,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    discounted?: boolean;
    orderId: string;
    paymentType: "recurring";
    ownerId: string;
    price: { amount: number; currency: Currency };
    productClientId: string;
    totalInstallments: number | null;
    paymentCycle: number;
    checkoutVariantId?: string | null;
    isOverlappingSubscription?: boolean;
    /**
     * The cycle is a free month granted by `add_free_days`, not an installment
     * (TRE-1724). Access still extends and the invoice, stats and automations
     * still run off this event — only the renewal notification is muted, since
     * there is no payment to report and `paymentCycle` deliberately stands
     * still.
     */
    isFreeExtensionCycle?: boolean;
  }
>;

export type ProductManualPaymentCreatedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductManualPaymentCreated,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    discounted?: boolean;
    orderId: string;
    paymentType: "recurring" | "one_time" | "installments";
    installments: number | null;
    ownerId: string;
    price: { amount: number; currency: Currency; amountForMainProduct: number };
    crossSell?: CrossSellProductInfo[];
    sendClientNotification?: boolean | null;
  }
>;

export type ProductManualPaymentForNextCycleCreatedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductManualPaymentForNextCycleCreated,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    discounted?: boolean;
    orderId: string;
    paymentType: "recurring" | "one_time" | "installments";
    installments: number | null;
    ownerId: string;
    price: { amount: number; currency: Currency };
    paymentCycle: number;
    productClientId: string;
  }
>;
export type ProductManualPaymentExpiredEventDto = GenericBusinessEventDto<
  ProductEventType.ProductManualPaymentExpired,
  {
    productId: string;
    clientEmail: string;
    customerId: string | null;
    priceId: string | null;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    // product_order id — legacy manual orders reuse the Stripe invoice id (in_...) here
    orderId: string;
    // unpaid Stripe invoice id (in_...) when the order stores one separately
    invoiceId: string | null;
    paymentType: "recurring" | "installments";
    installments: number | null;
    paymentCycle: number | null;
    ownerId: string;
    price: { amount: number; currency: Currency } | null;
    productClientId: string;
    invoiceDueDate: number;
    daysOverdue: number;
  }
>;

export type ProductInstallmentsPaidOffEventDto = GenericBusinessEventDto<
  ProductEventType.ProductInstallmentsPaidOff,
  {
    productId: string;
    productName: string;
    productClientId: string;
    clientEmail: string;
    customerId: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    ownerId: string;
    priceId: string | null;
    orderId: string;
    /** Sum of the paid-off installments before the discount, minor units. */
    remainingAmount: number;
    /** Amount actually charged, minor units. */
    paidAmount: number;
    discountPct: number;
    remainingInstallments: number;
    currency: Currency;
    installmentEndsAt: number | null;
  }
>;

export type ProductAccessProlongedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductAccessProlonged,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    orderId: string;
    paymentType: "recurring" | "one_time";
    ownerId: string;
    price: { amount: number; currency: Currency; description: string | null };
    installments: number | null;
    phaseStartDate: number | true;
    productClientId: string;
    orderNumber: number;
    crossSell?: CrossSellProductInfo[];
    checkoutVariantId: string | null;
  }
>;

export type ProductSubscriptionPlanUpdatedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductSubscriptionPlanUpdated,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    discounted?: boolean;
    orderId: string;
    paymentType: "recurring";
    ownerId: string;
    price: {
      amount: number;
      currency: Currency;
      baseAmount: number;
      interval_count: number;
      interval: ProductSubscriptionInterval;
    };
    prorated: boolean;
    previousPrice: {
      id: string;
      amount: number;
      currency: Currency;
      interval_count: number;
      interval: ProductSubscriptionInterval;
    };
  }
>;

export type ProductAccessRenewedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductAccessRenewed,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    discounted?: boolean;
    ownerEmail: string;
    orderId: string;
    paymentType: "one_time" | "installments";
    ownerId: string;
    price: { amount: number; currency: Currency };
    installments?: number | null;
    productClientId: string;
    checkoutVariantId?: string | null;
  }
>;

export type ProductPaymentFailedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductPaymentFailed,
  {
    productId: string;
    clientEmail: string;
    customerId: string | null;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    orderId: string;
    paymentType: "one_time" | "installments" | "recurring";
    ownerId: string;
    price: { amount: number; currency: Currency; label: string | null };
    installments?: number | null;
    paymentCycle: number | null;
    productClientId: string;
    isOverlapping?: boolean;
    /**
     * Set when a BLIK recurring (model A) installment could not be auto-charged
     * and the client was sent a link to pay it manually. `paymentUrl` is the
     * renewal checkout link (paying via BLIK there re-registers the alias).
     */
    blikRecurringFallback?: {
      fallbackLinkSent: boolean;
      paymentUrl: string;
      /**
       * The bank refused because the charge is above her BLIK limit, so the
       * e-mail tells her to raise it or pay by card (TRE-2246).
       */
      limitExceeded?: boolean;
    };
    /**
     * Why the gateway refused, in words — e.g. an exceeded BLIK limit, which
     * the client has to raise herself before any retry can work, as opposed to
     * insufficient funds, which just needs another go (TRE-2136).
     */
    declineReason?: string | null;
    /** Attempts made to collect this payment so far. */
    attemptCount?: number | null;
    /**
     * Stripe hosted invoice page for the failed charge — lets the client settle
     * the payment (or switch cards) without waiting for the automatic retry.
     * Only present for Stripe invoice failures (TRE-1894).
     */
    hostedInvoiceUrl?: string | null;
  }
>;

export type ProductSubscriptionEndEventDto = GenericBusinessEventDto<
  ProductEventType.ProductSubscriptionEnd,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string | null;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    ownerId: string;
    price: { amount: number; currency: Currency } | null;
    productClientId: string;
    installments: boolean;
  }
>;

export type ProductAccessPeriodEndEventDto = GenericBusinessEventDto<
  ProductEventType.ProductAccessPeriodEnd,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string | null;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    ownerId: string;
    price: { amount: number; currency: Currency } | null;
    reason?: ClientArchiveReason;
  }
>;

export type ProductCreatedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductCreated,
  {
    productId: string;
    productName: string;
    productChargeType: ProductChargeTypeEnum;
    ownerId: string;
  }
>;

export type ProductClientImportedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductClientImported,
  {
    productId: string;
    clientEmail: string;
    customerId: string;
    priceId: string | null;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    ownerEmail: string;
    isFree: boolean;
    orderId: string;
    importId: string;
    paymentType: PricePaymentType | null;
    productChargeType: ProductChargeTypeEnum;
    ownerId: string;
    price: null | { amount: number; currency: Currency };
    silent: boolean;
    clientRefCode?: string | null;
    createdClientId?: string | null;
  }
>;

export type ProductClientsImportFinishedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductClientsImportFinished,
  {
    productId: string;
    productName: string;
    ownerId: string;
    importId: string;
    ownerEmail: string;
    totalClients: number;
    failedImports: number;
    /** Rows settled as duplicates by policy `skip` (TRE-2110); absent on events from before. */
    skippedImports?: number;
    isFree: boolean;
  }
>;

export type ProductClientArchivedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductClientArchived,
  {
    productId: string;
    ownerId: string;
    ownerEmail: string;
    isFree: boolean;
    priceId: string | null;
    productName: string;
    clientEmail: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    refunded: boolean;
    refund: { amount: number; currency: Currency } | null;
    /** Order the refund was recorded on — consumers correcting its invoice need it. */
    orderId?: string | null;
    /** Issue a correction of the order's connected Fakturownia invoice (picked in the archive modal). */
    correctInvoice?: boolean;
    tasks: AutomationTaskWithoutTriggers[];
  }
>;

/**
 * Which trainee profile the client keeps when an e-mail change merges the product into an
 * account that already has a trainee of its own (TRE-2399). The other one is parked on the
 * old account.
 */
export const TraineeMergeStrategy = {
  /** trainee-service decides: the profile running on the merged, active product wins. */
  Auto: "auto",
  /** The profile from the old e-mail moves to the target account. */
  MoveFromOldAccount: "move_from_old_account",
  /** The target account keeps its own profile, activated with the merged product. */
  KeepTargetAccount: "keep_target_account",
} as const;

export type TraineeMergeStrategy = (typeof TraineeMergeStrategy)[keyof typeof TraineeMergeStrategy];

export type ProductClientEmailChangedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductClientEmailChanged,
  {
    productId: string;
    ownerId: string;
    customerId: string;
    ownerEmail: string;
    isFree: boolean;
    priceId: string | null;
    productName: string;
    clientEmail: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhoneNumber: string | null;
    oldEmail: string;
    tasksForOldEmail: AutomationTaskWithoutTriggers[];
    tasksForNewEmail: AutomationTaskWithoutTriggers[];
    userId: string | null;
    // When set, the product was merged into an already-existing client account (this user id)
    // instead of renaming/creating an account. Consumers must not rename the old entity by email.
    mergedIntoUserId?: string | null;
    // Only meaningful together with `mergedIntoUserId`; missing means `auto`.
    traineeMergeStrategy?: TraineeMergeStrategy;
  }
>;

export type ProductSubscriptionCancelAtPeriodEndEventDto = GenericBusinessEventDto<
  ProductEventType.ProductSubscriptionCancelAtPeriodEnd,
  {
    productId: string;
    clientEmail: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    ownerEmail: string;
    paymentType: "recurring";
    ownerId: string;
    price: { amount: number; currency: Currency };
    periodEnd: number;
    productClientId: string;
  }
>;

export type ProductSubscriptionResumeAtPeriodEndEventDto = GenericBusinessEventDto<
  ProductEventType.ProductSubscriptionResumeAtPeriodEnd,
  {
    productId: string;
    clientEmail: string;
    priceId: string;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    ownerEmail: string;
    paymentType: "recurring";
    ownerId: string;
    price: { amount: number; currency: Currency };
    periodEnd: number;
    productClientId: string;
  }
>;

export type ProductAccessPeriodEndingEventDto = GenericBusinessEventDto<
  | ProductEventType.ProductAccessPeriodEndingIn14Days
  | ProductEventType.ProductAccessPeriodEndingIn7Days
  | ProductEventType.ProductAccessPeriodEndingIn3Days
  | ProductEventType.ProductAccessPeriodEndingIn1Day,
  {
    productId: string;
    clientEmail: string;
    priceId: string | null;
    productName: string;
    clientFirstName: string;
    clientLastName: string;
    ownerEmail: string;
    ownerId: string;
    endAt: number;
    price: { amount: number; currency: Currency } | null;
    checkoutUrl: string;
  }
>;

export type ProductSlugChangedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductSlugChanged,
  {
    productId: string;
    productName: string;
    ownerEmail: string;
    isFree: boolean;
    ownerId: string;
    slug: string;
    oldSlug: string | null;
  }
>;

export type ProductClientRemovedEventDto = GenericBusinessEventDto<
  ProductEventType.ProductClientRemoved,
  {
    productId: string;
    ownerId: string;
    ownerEmail: string;
    isFree: boolean;
    priceId: string | null;
    productName: string;
    clientEmail: string;
    clientFirstName: string;
    clientLastName: string;
    tasks?: AutomationTaskWithoutTriggers[] | null;
  }
>;

export type ProductAccessRescheduledEventDto = GenericBusinessEventDto<
  ProductEventType.ProductAccessRescheduled,
  {
    productId: string;
    clientEmail: string;
    clientFirstName: string;
    clientLastName: string;
    productName: string;
    clientPhoneNumber: string | null;
    ownerId: string;
    ownerEmail: string;
    productClientId: string;
    currentEndAtAccess: number | null;
    currentAssignedProductClientId: string | null;
    currentAssignedProductId: string | null;
    priceId?: null;
  }
>;

export type ProductPurchasedEvent = GenericBusinessEvent<
  ProductPurchasedEventDto["eventType"],
  ProductPurchasedEventDto["payload"]
>;

export type ProductCreatedEvent = GenericBusinessEvent<
  ProductCreatedEventDto["eventType"],
  ProductCreatedEventDto["payload"]
>;

export type ProductSubscriptionRenewedEvent = GenericBusinessEvent<
  ProductSubscriptionRenewedEventDto["eventType"],
  ProductSubscriptionRenewedEventDto["payload"]
>;

export type ProductSubscriptionPlanUpdatedEvent = GenericBusinessEvent<
  ProductSubscriptionPlanUpdatedEventDto["eventType"],
  ProductSubscriptionPlanUpdatedEventDto["payload"]
>;

export type ProductAccessRenewedEvent = GenericBusinessEvent<
  ProductAccessRenewedEventDto["eventType"],
  ProductAccessRenewedEventDto["payload"]
>;

export type ProductSubscriptionEndEvent = GenericBusinessEvent<
  ProductSubscriptionEndEventDto["eventType"],
  ProductSubscriptionEndEventDto["payload"]
>;

export type ProductAccessPeriodEndEvent = GenericBusinessEvent<
  ProductAccessPeriodEndEventDto["eventType"],
  ProductAccessPeriodEndEventDto["payload"]
>;

export type ProductClientImportedEvent = GenericBusinessEvent<
  ProductClientImportedEventDto["eventType"],
  ProductClientImportedEventDto["payload"]
>;

export type ProductClientsImportFinishedEvent = GenericBusinessEvent<
  ProductClientsImportFinishedEventDto["eventType"],
  ProductClientsImportFinishedEventDto["payload"]
>;

export type ProductClientArchivedEvent = GenericBusinessEvent<
  ProductClientArchivedEventDto["eventType"],
  ProductClientArchivedEventDto["payload"]
>;

export type ProductClientEmailChangedEvent = GenericBusinessEvent<
  ProductClientEmailChangedEventDto["eventType"],
  ProductClientEmailChangedEventDto["payload"]
>;

export type ProductSubscriptionCancelAtPeriodEndEvent = GenericBusinessEvent<
  ProductSubscriptionCancelAtPeriodEndEventDto["eventType"],
  ProductSubscriptionCancelAtPeriodEndEventDto["payload"]
>;

export type ProductSubscriptionResumeAtPeriodEndEvent = GenericBusinessEvent<
  ProductSubscriptionResumeAtPeriodEndEventDto["eventType"],
  ProductSubscriptionResumeAtPeriodEndEventDto["payload"]
>;

export type ProductAccessPeriodEndingEvent = GenericBusinessEvent<
  ProductAccessPeriodEndingEventDto["eventType"],
  ProductAccessPeriodEndingEventDto["payload"]
>;

export type ProductSlugChangedEvent = GenericBusinessEvent<
  ProductSlugChangedEventDto["eventType"],
  ProductSlugChangedEventDto["payload"]
>;

export type ProductClientRemovedEvent = GenericBusinessEvent<
  ProductClientRemovedEventDto["eventType"],
  ProductClientRemovedEventDto["payload"]
>;

export type ProductAccessRescheduledEvent = GenericBusinessEvent<
  ProductAccessRescheduledEventDto["eventType"],
  ProductAccessRescheduledEventDto["payload"]
>;

export type ProductPaymentFailedEvent = GenericBusinessEvent<
  ProductPaymentFailedEventDto["eventType"],
  ProductPaymentFailedEventDto["payload"]
>;

export type ProductAccessProlongedEvent = GenericBusinessEvent<
  ProductAccessProlongedEventDto["eventType"],
  ProductAccessProlongedEventDto["payload"]
>;

export type ProductManualPaymentCreatedEvent = GenericBusinessEvent<
  ProductManualPaymentCreatedEventDto["eventType"],
  ProductManualPaymentCreatedEventDto["payload"]
>;

export type ProductManualPaymentForNextCycleCreatedEvent = GenericBusinessEvent<
  ProductManualPaymentForNextCycleCreatedEventDto["eventType"],
  ProductManualPaymentForNextCycleCreatedEventDto["payload"]
>;

export type ProductManualPaymentExpiredEvent = GenericBusinessEvent<
  ProductManualPaymentExpiredEventDto["eventType"],
  ProductManualPaymentExpiredEventDto["payload"]
>;

export type ProductInstallmentsPaidOffEvent = GenericBusinessEvent<
  ProductInstallmentsPaidOffEventDto["eventType"],
  ProductInstallmentsPaidOffEventDto["payload"]
>;

export type ProductEventsDto =
  | ProductPurchasedEventDto
  | ProductCreatedEventDto
  | ProductSubscriptionRenewedEventDto
  | ProductSubscriptionEndEventDto
  | ProductAccessPeriodEndEventDto
  | ProductClientImportedEventDto
  | ProductClientsImportFinishedEventDto
  | ProductSubscriptionCancelAtPeriodEndEventDto
  | ProductSubscriptionResumeAtPeriodEndEventDto
  | ProductAccessPeriodEndingEventDto
  | ProductAccessRenewedEventDto
  | ProductClientArchivedEventDto
  | ProductSubscriptionPlanUpdatedEventDto
  | ProductClientEmailChangedEventDto
  | ProductClientRemovedEventDto
  | ProductSlugChangedEventDto
  | ProductAccessRescheduledEventDto
  | ProductPaymentFailedEventDto
  | ProductAccessProlongedEventDto
  | ProductManualPaymentCreatedEventDto
  | ProductManualPaymentForNextCycleCreatedEventDto
  | ProductManualPaymentExpiredEventDto
  | ProductInstallmentsPaidOffEventDto;

export type ProductEvents =
  | ProductPurchasedEvent
  | ProductCreatedEvent
  | ProductSubscriptionRenewedEvent
  | ProductSubscriptionEndEvent
  | ProductAccessPeriodEndEvent
  | ProductClientImportedEvent
  | ProductClientsImportFinishedEvent
  | ProductSubscriptionCancelAtPeriodEndEvent
  | ProductSubscriptionResumeAtPeriodEndEvent
  | ProductAccessPeriodEndingEvent
  | ProductAccessRenewedEvent
  | ProductSubscriptionPlanUpdatedEvent
  | ProductClientArchivedEvent
  | ProductClientEmailChangedEvent
  | ProductSlugChangedEvent
  | ProductClientRemovedEvent
  | ProductAccessRescheduledEvent
  | ProductPaymentFailedEvent
  | ProductAccessProlongedEvent
  | ProductManualPaymentCreatedEvent
  | ProductManualPaymentForNextCycleCreatedEvent
  | ProductManualPaymentExpiredEvent
  | ProductInstallmentsPaidOffEvent;

export type ProductEventsPayload =
  | ProductPurchasedEventDto["payload"]
  | ProductCreatedEvent["payload"]
  | ProductSubscriptionRenewedEvent["payload"]
  | ProductSubscriptionEndEvent["payload"]
  | ProductAccessPeriodEndEvent["payload"]
  | ProductClientImportedEvent["payload"]
  | ProductClientsImportFinishedEvent["payload"]
  | ProductSubscriptionCancelAtPeriodEndEvent["payload"]
  | ProductSubscriptionResumeAtPeriodEndEvent["payload"]
  | ProductAccessPeriodEndingEvent["payload"]
  | ProductAccessRenewedEvent["payload"]
  | ProductSubscriptionPlanUpdatedEvent["payload"]
  | ProductClientArchivedEvent["payload"]
  | ProductClientEmailChangedEvent["payload"]
  | ProductSlugChangedEvent["payload"]
  | ProductClientRemovedEvent["payload"]
  | ProductAccessRescheduledEvent["payload"]
  | ProductPaymentFailedEvent["payload"]
  | ProductAccessProlongedEvent["payload"]
  | ProductManualPaymentCreatedEvent["payload"]
  | ProductManualPaymentForNextCycleCreatedEvent["payload"]
  | ProductManualPaymentExpiredEvent["payload"]
  | ProductInstallmentsPaidOffEvent["payload"];
